import React, { useEffect } from 'react';
import { SEOData } from '../services/api';

interface SEOHeadProps {
  seoData: SEOData;
}

const SEOHead: React.FC<SEOHeadProps> = ({ seoData }) => {
  useEffect(() => {
    // Update document title
    if (seoData.meta_title) {
      document.title = seoData.meta_title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updatePropertyTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Basic meta tags
    if (seoData.meta_description) {
      updateMetaTag('description', seoData.meta_description);
    }
    if (seoData.meta_keywords) {
      updateMetaTag('keywords', seoData.meta_keywords);
    }

    // Robots meta tag based on API data
    const robotsValue = [];
    if (seoData.robots_index !== false) robotsValue.push('index');
    else robotsValue.push('noindex');
    if (seoData.robots_follow !== false) robotsValue.push('follow');
    else robotsValue.push('nofollow');
    updateMetaTag('robots', robotsValue.join(', '));

    // Additional SEO meta tags
    updateMetaTag('author', 'Rawasi Kristal');
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0');
    updateMetaTag('format-detection', 'telephone=no');
    updateMetaTag('theme-color', '#000000');

    // Canonical URL
    if (seoData.canonical_url) {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = seoData.canonical_url;
    }

    // Open Graph tags
    if (seoData.og_title) {
      updatePropertyTag('og:title', seoData.og_title);
    }
    if (seoData.og_description) {
      updatePropertyTag('og:description', seoData.og_description);
    }
    if (seoData.og_image) {
      updatePropertyTag('og:image', seoData.og_image);
    }
    if (seoData.og_type) {
      updatePropertyTag('og:type', seoData.og_type);
    }
    if (seoData.og_url) {
      updatePropertyTag('og:url', seoData.og_url);
    }
    if (seoData.og_site_name) {
      updatePropertyTag('og:site_name', seoData.og_site_name);
    } else {
      updatePropertyTag('og:site_name', 'Rawasi Kristal');
    }
    updatePropertyTag('og:locale', 'ar_SA');

    // Twitter Card tags
    if (seoData.twitter_card) {
      updateMetaTag('twitter:card', seoData.twitter_card);
    }
    if (seoData.twitter_title) {
      updateMetaTag('twitter:title', seoData.twitter_title);
    }
    if (seoData.twitter_description) {
      updateMetaTag('twitter:description', seoData.twitter_description);
    }
    if (seoData.twitter_image) {
      updateMetaTag('twitter:image', seoData.twitter_image);
    }
    if (seoData.twitter_site) {
      updateMetaTag('twitter:site', seoData.twitter_site);
    } else {
      updateMetaTag('twitter:site', '@rawasicrystal');
    }
    if (seoData.twitter_creator) {
      updateMetaTag('twitter:creator', seoData.twitter_creator);
    }

    // JSON-LD structured data
    if (seoData.json_ld) {
      let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = seoData.json_ld;
    } else {
      // Default structured data
      const defaultStructuredData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Rawasi Kristal",
        "description": "Professional interior design and decoration services in Saudi Arabia",
        "url": "https://rawasicrystal.com",
        "logo": "https://rawasicrystal.com/logo.png",
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service"
        },
        "sameAs": [
          "https://www.facebook.com/rawasicrystal",
          "https://www.instagram.com/rawasicrystal"
        ]
      };
      
      let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(defaultStructuredData);
    }

    // Language
    if (seoData.language) {
      document.documentElement.lang = seoData.language;
    } else {
      document.documentElement.lang = 'ar';
    }

    // Custom meta tags from API
    if (seoData.custom_meta_tags) {
      try {
        const customTags = JSON.parse(seoData.custom_meta_tags);
        if (Array.isArray(customTags)) {
          customTags.forEach(tag => {
            if (tag.name && tag.content) {
              updateMetaTag(tag.name, tag.content);
            } else if (tag.property && tag.content) {
              updatePropertyTag(tag.property, tag.content);
            }
          });
        }
      } catch (error) {
        console.warn('Failed to parse custom meta tags:', error);
      }
    }

    // Additional performance hints
    const addResourceHint = (rel: string, href: string, as?: string) => {
      let link = document.querySelector(`link[rel="${rel}"][href="${href}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        link.href = href;
        if (as) link.setAttribute('as', as);
        document.head.appendChild(link);
      }
    };

    // Add resource hints for better performance
    addResourceHint('preconnect', 'https://rawasicrystal.com');
    addResourceHint('dns-prefetch', '//rawasicrystal.com');

  }, [seoData]);

  return null;
};

export default SEOHead; 